import { ReactNode } from 'react';

import {
  alpha,
  Box,
  Card,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';

import { FavoriteHeartButton } from '@/shared/ui/components/button/FavoriteHeart';

import { getAgeDeclension } from '@/shared/lib/plural';

import { CardListRow, joinNames } from './CardListRow';
import { CardNameLine } from './CardNameLine';
import { ROW_CONTAINER, ROW_VALUE_SX, WIDE_ROW } from './cardRowStyles';
import { IUserCardBase, IUserCardViewProps } from './CardTypes';
import { UserCardCornerActions } from './UserCardCornerActions';
import { useUserCardActions } from './useUserCardActions';

/**
 * Сколько превью показывает лента. Бэкенд уже режет выдачу до 10, но лента не
 * полагается на это: каждая лишняя плитка — лишняя ссылка на вариант изображения и
 * лишнее обращение к хранилищу, а список рисует десятки карточек за раз.
 */
const PREVIEWS_LIMIT = 10;

/**
 * Сколько превью лента считает «полной». Ниже этого числа кадры не набирают ряд, и
 * без подписи он читается как незагрузившийся: подпись объясняет, что портфолио
 * действительно короткое.
 */
const CAPTION_THRESHOLD = 3;

/**
 * Высота ряда ленты. Фиксирована намеренно: у превью разные пропорции, и без
 * фиксации ряд плясал бы от самого «высокого» кадра в выдаче — кадры вписываются
 * обрезкой (`object-fit: cover`), а не растягивают ряд под себя.
 */
const TILE_HEIGHT = 128;

/** Тот же ряд в широкой колонке */
const TILE_HEIGHT_WIDE = 144;

/** Отступ ленты снизу — запас под полосу прокрутки (8px и 12px) */
const STRIP_PAD = 8;

/** Тот же запас в широкой колонке */
const STRIP_PAD_WIDE = 12;

/**
 * Лента превью: нативная горизонтальная прокрутка вместо карусели. Swiper в проекте
 * приколочен к версии и тянет свою вёрстку; здесь он не нужен — плиток максимум
 * десять, им нужен именно скролл, а не слайдер с состоянием.
 */
const STRIP_SX: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  overflowX: 'auto',
  overflowY: 'hidden',
  // Прокрутка ленты не должна утягивать за собой прокрутку страницы
  overscrollBehaviorX: 'contain',
  scrollSnapType: 'x proximity',
  scrollbarWidth: 'thin',
  alignItems: 'flex-start',
  px: 2,
  // `scroll-padding-left` — тот же отступ, что и `px` (18976, замер U):
  // `scroll-snap-type` без него считает точкой покоя первой плитки
  // `scrollLeft = paddingLeft`, а не 0 — левый паддинг визуально съедается
  // снапом при любой прокрутке ленты (включая начальную, программную или по
  // клавиатуре), хотя сам паддинг честно объявлен и виден в `getComputedStyle`.
  /**
   * `scroll-padding-left` того же размера, что и `px` ленты — см. комментарий
   * выше про снап первой плитки.
   * @param theme - тема MUI
   */
  scrollPaddingLeft: theme => theme.spacing(2),
  pb: 2,
  height: TILE_HEIGHT + STRIP_PAD,
  [WIDE_ROW]: {
    px: 3,
    /**
     * `scroll-padding-left` широкой колонки — тот же приём, что и в узкой,
     * значение растёт вместе с `px: 3`.
     * @param theme - тема MUI
     */
    scrollPaddingLeft: theme => theme.spacing(3),
    pb: 3,
    height: TILE_HEIGHT_WIDE + STRIP_PAD_WIDE
  },
  '&::-webkit-scrollbar': { height: 6 },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 3,
    backgroundColor: 'action.disabled'
  }
};

/**
 * Плитка превью. Пропорция 3/4 — та же, что у фото во всех остальных видах карточки,
 * так что лента читается как продолжение галереи, а не как чужой блок. Внутренняя
 * рамка нужна светлым кадрам: без неё белое фото сливается с белой карточкой.
 *
 * Источник — `photos[].url`, с 19244 это вариант 195×260 (3:4) вместо квадрата
 * 130×130, растянутого в плитку; `object-fit: cover` держит старые квадраты без
 * искажения. Фон `grey.200` — единый фон рамок изображений под незагруженной
 * картинкой (19243).
 */
const TILE_SX: SxProps<Theme> = {
  width: 96,
  height: TILE_HEIGHT,
  flexShrink: 0,
  objectFit: 'cover',
  backgroundColor: 'grey.200',
  display: 'block',
  scrollSnapAlign: 'start',
  /**
   * Внутренняя рамка плитки: цвет только из темы, без сырого rgba
   * @param theme - тема MUI
   */
  boxShadow: (theme: Theme) =>
    `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.06)}`,
  [WIDE_ROW]: { width: 108, height: TILE_HEIGHT_WIDE }
};

/** Подпись «N фото в портфолио» под короткой лентой */
const CAPTION_SX: SxProps<Theme> = {
  ...ROW_VALUE_SX,
  color: 'text.disabled',
  px: 2,
  pb: 2,
  // Кегль широкой колонки берём из ROW_VALUE_SX: ключ WIDE_ROW один, и без
  // повтора значения он затёр бы наследованный fontSize
  [WIDE_ROW]: { fontSize: '14px', px: 3, pb: 3 }
};

/**
 * Приписка вырожденной ленты. Не «ошибка загрузки» и не «фото скрыты»: с точки
 * зрения экрана оба состояния (ключа `photos` в ответе нет, портфолио пусто)
 * выглядят одинаково — показывать нечего, — и различать их формулировкой не на
 * чем.
 */
const EMPTY_PORTFOLIO_TEXT = 'Без фото в портфолио';

/**
 * Шапка ленты: имя, возраст и контекст экрана. Раньше резервировала 88px
 * (`ROW_HEART_RESERVE`) под пару [папка][сердце] — оверлей в правом верхнем
 * углу карточки, но реального содержимого рядом с именем, чтобы враз тратить
 * 88px на каждой ширине, не бывает: сердце и папка теперь просто плывут поверх
 * шапки, а не отнимают у неё текстовую колонку (18976, Р3).
 */
const HEADER_SX: SxProps<Theme> = {
  gap: 1,
  p: 2,
  [WIDE_ROW]: { p: 3 }
};

/**
 * Инсет угловой группы [папка][сердце] — СОБСТВЕННЫЙ паддинг карточки (18976,
 * Р76): раньше группа стояла вплотную к углу (`top: 0, right: 0`), без
 * единого отступа с остальным содержимым карточки (шапка — `HEADER_SX`, лента
 * — `STRIP_SX`, подпись — `CAPTION_SX`, у всех те же `theme.spacing(2)`/`(3)`
 * на том же пороге `WIDE_ROW`). Заказчик потребовал того же отступа, что и у
 * карточки («я вижу что это 8px») — приём и числа те же, что несёт
 * `CardListRow#getHeartSx`.
 * @param theme - тема MUI
 */
const getCornerActionsSx = (theme: Theme): SxProps<Theme> => {
  const inset = theme.spacing(2);

  return {
    top: inset,
    right: inset,
    [WIDE_ROW]: { top: theme.spacing(3), right: theme.spacing(3) }
  };
};

/**
 * Строка «имя + плашка статуса» (18976, Р77): статус переехал из полосы под
 * лентой (`footer`, растянутая на всю ширину пилюля — см. `X-status-design.md`)
 * в шапку, рядом с именем, естественной ширины. `wrap` — на узкой шапке (375px,
 * имя уже режется до «Никитос Ко…») плашке некуда деться в одну строку с
 * именем, и она уходит на вторую линию целиком, а не обрезается.
 */
const HEADER_NAME_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
  rowGap: 1
};

interface IHeaderProps {
  user: IUserCardBase;
  additionalInfo?: string;
  statusSlot?: () => ReactNode;
}

/**
 * Шапка карточки-ленты: имя (плюс плашка статуса, 18976, Р77), возраст, города
 * и, если экран его передал, контекст (кастинг).
 *
 * Города здесь потому, что без них лента была ИНФОРМАЦИОННО БЕДНЕЕ компактного
 * ряда: тот показывает город от 420px, а лента не показывала его нигде — и вид,
 * который занимает вдвое больше места, знал об актёре меньше. Кегль и цвет — те
 * же `ROW_VALUE_SX`, что у возраста: это такое же значение без подписи, а не
 * новая иерархия. Порога у них нет, в отличие от компактного ряда: в шапке ленты
 * города стоят своей строкой и ни с чем за место не спорят.
 * @param props - пропсы
 * @param props.user - данные актёра
 * @param props.additionalInfo - дополнительная информация (например, название кастинга)
 * @param props.statusSlot - плашка статуса естественной ширины, рядом с именем
 */
const StripHeader = ({ user, additionalInfo, statusSlot }: IHeaderProps) => {
  const age = user.age ? getAgeDeclension(user.age) : '';
  const cities = joinNames(user.cities);

  return (
    <Stack sx={HEADER_SX}>
      <Box sx={HEADER_NAME_ROW_SX}>
        <CardNameLine
          firstName={user.first_name}
          lastName={user.last_name}
          isVerified={user.system_photo_attached}
        />
        {statusSlot?.()}
      </Box>
      {!!age && <Typography sx={ROW_VALUE_SX}>{age}</Typography>}
      {!!cities && <Typography sx={ROW_VALUE_SX}>{cities}</Typography>}
      {!!additionalInfo && (
        <Typography sx={ROW_VALUE_SX}>{additionalInfo}</Typography>
      )}
    </Stack>
  );
};

/**
 * Вид `photos`: шапка с именем и возрастом плюс горизонтальная лента превью
 * портфолио.
 *
 * Превью может не быть вовсе: ключа `photos` в ответе нет (список не запросил
 * превью либо профиль скрыт настройками видимости) либо портфолио пусто. Ленте в
 * этом случае нечего показывать, и вместо карточки высотой 144px с одной
 * плиткой-заглушкой такой актёр получает КОМПАКТНЫЙ РЯД — те же имя, возраст и
 * аватар втрое ниже.
 *
 * Но не безмолвный компактный ряд: с припиской «Без фото в портфолио» он
 * ОТЛИЧИМ от настоящего вида `compact`. Без неё выбор вида «фото» на актёре с
 * пустым портфолио не менял на экране ничего, и вид читался как незаработавший
 * переключатель. Приписка приглушена (`text.disabled`) намеренно: пустое
 * портфолио — нормальное состояние, а не ошибка, и предупреждением она быть не
 * должна.
 *
 * Короткая лента (меньше трёх кадров) получает подпись «N фото в портфолио»: без
 * неё полупустой ряд читается как незагрузившийся.
 *
 * Признака «главное фото» у превью нет: порядок — это порядок портфолио, ровно тот,
 * что видит сам актёр в галерее, и выделять в ленте первую плитку нечем и незачем.
 * @param props - пропсы
 * @param props.user - данные актёра из списочного ответа
 * @param props.sx - стили карточки
 * @param props.link - ссылка перехода, если экрану нужна не карточка профиля
 * @param props.bottomContent - слот действий (пара решения); статус (18976,
 * Р77) сюда больше не приходит — см. `props.statusSlot`
 * @param props.additionalInfo - дополнительная информация (например, название кастинга)
 * @param props.renderFolderAction - слот кнопки «в папку», рисуется рядом с сердцем
 * @param props.onBookmarked - снекбар «добавлен в избранное» экрана (см. `IUserCardViewProps`)
 * @param props.onUnbookmarked - снекбар «убрано из избранного» экрана
 * @param props.onQuickView - открыть окно быстрого просмотра вместо перехода на анкету
 * @param props.statusSlot - плашка статуса естественной ширины (18976, Р77) —
 * в шапку, рядом с именем (см. `StripHeader`); полоса под лентой её больше не
 * получает
 * @param props.hasStatusContent - есть ли у `statusSlot` что показать; булев
 * гейт обязателен: сам слот — функция, а её результат (JSX-элемент) истинен
 * даже когда компонент внутри вернёт `null`
 * @param props.denseStatusSlot - вырожденная лента (без превью) деградирует в
 * `CardListRow`, слот пробрасывается ему как есть (см. её JSDoc)
 * @param props.hasDenseStatus - см. `denseStatusSlot`
 * @param props.decisionSlot - вырожденная лента деградирует в `CardListRow`,
 * слот пробрасывается ему как есть
 * @param props.hasDecisionContent - есть ли у `decisionSlot` что показать; та
 * же причина, что у `hasStatusContent`
 */
export const CardPhotoStrip = <T extends IUserCardBase>({
  user,
  sx,
  link,
  bottomContent,
  additionalInfo,
  renderFolderAction,
  onBookmarked,
  onUnbookmarked,
  onQuickView,
  statusSlot,
  hasStatusContent,
  denseStatusSlot,
  hasDenseStatus,
  decisionSlot,
  hasDecisionContent
}: IUserCardViewProps<T>) => {
  const theme = useTheme();
  const { shape, shadows } = theme;

  const {
    actorLink,
    folderIds,
    handleBookmarkClick,
    handleCardClick,
    handleFolderIdsChange,
    isBookmarked,
    isLoadingBookmarkUser
  } = useUserCardActions(user, link, {
    onBookmarked,
    onUnbookmarked,
    onQuickView
  });

  const previews = (user.photos ?? []).slice(0, PREVIEWS_LIMIT);
  const target = user.visible ? actorLink : '#';
  const tileSx = { ...TILE_SX, borderRadius: shape.borderM };
  /**
   * `bottomContent` вызывается один раз и проверяется по результату: экран
   * ОБЯЗАН передавать сюда `undefined`, когда содержимому нечего показать
   * (18976, Р77) — в отличие от `statusSlot`/`decisionSlot` каркаса строки
   * (`hasStatusContent`/`hasDecisionContent`), у `bottomContent` нет булева
   * флага-компаньона: это универсальный слот произвольного содержимого
   * (используется и видом `card`), а не типизированный контракт статуса
   * отклика. Экран, оборачивающий сюда `ResponseStatusBlock`, решает вопрос
   * «звать ли слот вовсе» САМ, чистой функцией (`isDecisionPending`), ДО
   * передачи пропа — см. `ResponsesListContent`.
   */
  const bottomContentResult = bottomContent?.();

  /**
   * Ссылка-обёртка плитки: клик по любой превью ведёт туда же, куда клик по карточке
   * @param key - ключ элемента ленты
   * @param children - содержимое плитки
   */
  const renderTileLink = (key: string, children: ReactNode) => (
    <Link
      key={key}
      to={target}
      onClick={handleCardClick}
      style={{ display: 'block', flexShrink: 0, lineHeight: 0 }}
    >
      {children}
    </Link>
  );

  /** Плитки портфолио */
  const renderTiles = () =>
    previews.map((photo, index) =>
      renderTileLink(
        String(photo.id),
        <Box
          component='img'
          src={photo.url}
          alt={`Фото портфолио ${index + 1}`}
          loading='lazy'
          sx={tileSx}
        />
      )
    );

  if (!previews.length) {
    return (
      <CardListRow
        user={user}
        variant='compact'
        sx={sx}
        link={link}
        additionalInfo={additionalInfo}
        metaSuffix={EMPTY_PORTFOLIO_TEXT}
        renderFolderAction={renderFolderAction}
        statusSlot={statusSlot}
        // Булевы гейты идут ВМЕСТЕ со своими слотами: у `CardListRow` дефолт
        // `false`, и без них вырожденная лента (пустое портфолио) молча гасила
        // бы плашку статуса и пару решения — слот пришёл, а гейт сказал «пусто»
        hasStatusContent={hasStatusContent}
        denseStatusSlot={denseStatusSlot}
        hasDenseStatus={hasDenseStatus}
        // Пара решения уходит в `decisionSlot` каркаса строки (18976, Р77) —
        // НЕ в `bottomContent`/`footer`: тот же контент, отданный сюда ЖЕ
        // строкой ниже, задвоил бы кнопки (полоса-футер и угол `compact`
        // одновременно)
        decisionSlot={decisionSlot}
        hasDecisionContent={hasDecisionContent}
      />
    );
  }

  return (
    <Card
      sx={{
        p: 0,
        position: 'relative',
        width: '100%',
        containerType: 'inline-size',
        containerName: ROW_CONTAINER,
        // Тень вместо серой заливки — тот же приём наведения, что у
        // `getRowSurfaceSx` строчных видов и у `BaseProjectCard`/
        // `BaseCastingCard`/`BaseParticipantCard` (18976, Р5): заливка
        // осталась чужеродной для карточки контента
        ':hover': { boxShadow: shadows[6] },
        ...sx
      }}
    >
      <Link
        to={target}
        style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}
        onClick={handleCardClick}
      >
        <StripHeader
          user={user}
          additionalInfo={additionalInfo}
          statusSlot={statusSlot}
        />
      </Link>

      <Box sx={STRIP_SX}>{renderTiles()}</Box>

      {previews.length < CAPTION_THRESHOLD && (
        <Typography sx={CAPTION_SX}>
          {`${previews.length} фото в портфолио`}
        </Typography>
      )}

      <UserCardCornerActions
        sx={getCornerActionsSx(theme)}
        folder={renderFolderAction?.({
          targetId: Number(user.id),
          folderIds,
          isBookmarked,
          surface: 'light',
          onFolderIdsChange: handleFolderIdsChange
        })}
        heart={
          <FavoriteHeartButton
            surface='light'
            isBookmarked={isBookmarked}
            onClick={handleBookmarkClick}
            isLoading={isLoadingBookmarkUser}
            sx={{ position: 'static' }}
          />
        }
      />

      {bottomContentResult && (
        <Box sx={{ px: 2, pb: 2, [WIDE_ROW]: { px: 3, pb: 3 } }}>
          {bottomContentResult}
        </Box>
      )}
    </Card>
  );
};
